<template>
  <AdminLayout>
    <div class="event-manage">
      <!-- 페이지 헤더 -->
      <div class="page-header">
        <div class="header-info">
          <div class="header-icon">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/>
            </svg>
          </div>
          <div>
            <h1 class="page-title">이벤트 관리</h1>
            <p class="page-subtitle">이벤트를 등록하고 참여자를 관리합니다</p>
          </div>
        </div>
        <button class="add-btn" @click="openCreateModal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          새 이벤트
        </button>
      </div>

      <!-- 통계 요약 -->
      <section class="stats-section">
        <div class="stat-card">
          <span class="stat-value">{{ events.length }}</span>
          <span class="stat-label">전체</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ activeCount }}</span>
          <span class="stat-label">진행중</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ totalParticipants }}</span>
          <span class="stat-label">총 참여자</span>
        </div>
      </section>

      <!-- 이벤트 목록 -->
      <section class="event-list">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>로딩 중...</p>
        </div>

        <div v-else-if="events.length === 0" class="empty-state">
          <span class="empty-icon">🎁</span>
          <p>등록된 이벤트가 없습니다</p>
          <button class="create-first-btn" @click="openCreateModal">
            첫 번째 이벤트 등록하기
          </button>
        </div>

        <div v-else class="event-items">
          <div
            v-for="event in events"
            :key="event.id"
            class="event-item"
            :class="{ inactive: !event.isActive, expired: isExpired(event) }"
          >
            <div class="event-header">
              <div class="event-badges">
                <span v-if="isOngoing(event)" class="badge ongoing">진행중</span>
                <span v-else-if="isExpired(event)" class="badge expired">종료</span>
                <span v-else class="badge upcoming">예정</span>
                <span v-if="!event.isActive" class="badge inactive">비활성</span>
                <span class="badge type" :class="event.participationType">
                  {{ event.participationType === 'direct' ? '직접참여' : '링크이동' }}
                </span>
              </div>
              <div class="event-actions">
                <button class="action-btn view" @click="openParticipantsModal(event)">
                  참여자 {{ event.participantCount || 0 }}
                </button>
                <button
                  class="action-btn"
                  :class="event.isActive ? 'deactivate' : 'activate'"
                  @click="handleToggleActive(event)"
                >
                  {{ event.isActive ? '비활성화' : '활성화' }}
                </button>
                <button class="action-btn edit" @click="openEditModal(event)">수정</button>
                <button class="action-btn delete" @click="handleDelete(event)">삭제</button>
              </div>
            </div>

            <h3 class="event-title">{{ event.title }}</h3>

            <div class="event-period">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>{{ formatPeriod(event) }}</span>
            </div>

            <div class="event-meta">
              <span class="meta-item">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                {{ event.viewCount?.toLocaleString() || 0 }}
              </span>
              <span class="meta-item">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                {{ event.participantCount?.toLocaleString() || 0 }}명 참여
              </span>
            </div>

            <div class="event-preview" v-html="getPreviewContent(event.content)"></div>
          </div>
        </div>
      </section>

      <!-- 이벤트 생성/수정 모달 -->
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">
          <div class="modal-header">
            <h2 class="modal-title">{{ isEditing ? '이벤트 수정' : '새 이벤트' }}</h2>
            <button class="close-btn" @click="closeModal">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label class="form-label">제목 <span class="required">*</span></label>
              <input
                v-model="form.title"
                type="text"
                class="form-input"
                placeholder="이벤트 제목을 입력하세요"
              />
            </div>

            <!-- 이벤트 기간 -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">시작일 <span class="required">*</span></label>
                <input
                  v-model="form.startDate"
                  type="date"
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label class="form-label">종료일</label>
                <input
                  v-model="form.endDate"
                  type="date"
                  class="form-input"
                />
                <p class="form-hint">비워두면 기간 제한 없음</p>
              </div>
            </div>

            <!-- 참여 방식 -->
            <div class="form-group">
              <label class="form-label">참여 방식 <span class="required">*</span></label>
              <div class="radio-group">
                <label class="radio-label">
                  <input
                    v-model="form.participationType"
                    type="radio"
                    value="direct"
                    name="participationType"
                  />
                  <span class="radio-mark"></span>
                  <div class="radio-content">
                    <span class="radio-title">직접 참여</span>
                    <span class="radio-desc">이름, 전화번호를 입력하여 참여</span>
                  </div>
                </label>
                <label class="radio-label">
                  <input
                    v-model="form.participationType"
                    type="radio"
                    value="link"
                    name="participationType"
                  />
                  <span class="radio-mark"></span>
                  <div class="radio-content">
                    <span class="radio-title">외부 링크</span>
                    <span class="radio-desc">외부 페이지로 이동하여 참여</span>
                  </div>
                </label>
              </div>
            </div>

            <!-- 외부 링크 입력 (링크 방식일 때만) -->
            <div v-if="form.participationType === 'link'" class="form-group">
              <label class="form-label">외부 링크 URL <span class="required">*</span></label>
              <input
                v-model="form.externalLink"
                type="url"
                class="form-input"
                placeholder="https://example.com/event"
              />
            </div>

            <!-- 썸네일 이미지 업로드 -->
            <div class="form-group">
              <label class="form-label">썸네일 이미지</label>
              <div class="thumbnail-upload">
                <div v-if="form.thumbnailUrl" class="thumbnail-preview">
                  <img :src="form.thumbnailUrl" alt="썸네일 미리보기" />
                  <button type="button" class="remove-thumbnail" @click="removeThumbnail">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                <label v-else class="upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    @change="handleThumbnailUpload"
                    hidden
                  />
                  <div class="upload-content">
                    <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span v-if="thumbnailUploading">업로드 중...</span>
                    <span v-else>클릭하여 이미지 업로드</span>
                    <span class="upload-hint">권장: 16:9 비율, 최대 5MB</span>
                  </div>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">내용 <span class="required">*</span></label>

              <!-- WYSIWYG 에디터 -->
              <div class="wysiwyg-editor">
                <!-- 에디터 툴바 -->
                <div class="editor-toolbar">
                  <!-- 텍스트 스타일 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('bold') }"
                      @click="execCommand('bold')"
                      title="굵게 (Ctrl+B)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('italic') }"
                      @click="execCommand('italic')"
                      title="기울임 (Ctrl+I)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: isFormatActive('underline') }"
                      @click="execCommand('underline')"
                      title="밑줄 (Ctrl+U)"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 글자 크기 -->
                  <div class="toolbar-group">
                    <select class="toolbar-select" @change="changeFontSize($event)" title="글자 크기">
                      <option value="">크기</option>
                      <option value="1">아주 작게</option>
                      <option value="2">작게</option>
                      <option value="3">보통</option>
                      <option value="4">크게</option>
                      <option value="5">아주 크게</option>
                      <option value="6">매우 크게</option>
                      <option value="7">최대</option>
                    </select>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 색상 -->
                  <div class="toolbar-group">
                    <div class="color-picker-wrapper">
                      <button type="button" class="toolbar-btn color-btn" title="글자 색상">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11 2L5.5 16h2.25l1.12-3h6.25l1.12 3h2.25L13 2h-2zm-1.38 9L12 4.67 14.38 11H9.62z"/>
                        </svg>
                        <span class="color-indicator" :style="{ backgroundColor: currentTextColor }"></span>
                      </button>
                      <input type="color" class="color-input" v-model="currentTextColor" @input="changeTextColor" title="글자 색상 선택">
                    </div>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 정렬 -->
                  <div class="toolbar-group">
                    <button type="button" class="toolbar-btn" @click="execCommand('justifyLeft')" title="왼쪽 정렬">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                    <button type="button" class="toolbar-btn" @click="execCommand('justifyCenter')" title="가운데 정렬">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                    <button type="button" class="toolbar-btn" @click="execCommand('justifyRight')" title="오른쪽 정렬">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 목록 -->
                  <div class="toolbar-group">
                    <button type="button" class="toolbar-btn" @click="execCommand('insertUnorderedList')" title="글머리 기호">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/>
                      </svg>
                    </button>
                    <button type="button" class="toolbar-btn" @click="execCommand('insertOrderedList')" title="번호 매기기">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- 링크, 이미지, 구분선 -->
                  <div class="toolbar-group">
                    <button type="button" class="toolbar-btn" @click="insertLink" title="링크 삽입">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                    </button>
                    <label class="toolbar-btn image-upload-btn" title="이미지 삽입">
                      <input
                        type="file"
                        accept="image/*"
                        @change="handleContentImageUpload"
                        hidden
                      />
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                      </svg>
                    </label>
                    <button type="button" class="toolbar-btn" @click="insertHorizontalRule" title="구분선 삽입">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4 11h16v2H4z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- Undo/Redo -->
                  <div class="toolbar-group">
                    <button type="button" class="toolbar-btn" @click="execCommand('undo')" title="실행 취소">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                      </svg>
                    </button>
                    <button type="button" class="toolbar-btn" @click="execCommand('redo')" title="다시 실행">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/>
                      </svg>
                    </button>
                  </div>

                  <span class="toolbar-divider"></span>

                  <!-- HTML 소스 -->
                  <div class="toolbar-group">
                    <button
                      type="button"
                      class="toolbar-btn"
                      :class="{ active: showHtmlSource }"
                      @click="toggleHtmlSource"
                      title="HTML 소스 보기"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- 에디터 영역 -->
                <div
                  v-show="!showHtmlSource"
                  ref="editorRef"
                  class="editor-content"
                  contenteditable="true"
                  @input="onEditorInput"
                  @keydown="onKeydown"
                  @mouseup="updateToolbarState"
                  @keyup="updateToolbarState"
                ></div>

                <!-- HTML 소스 편집 -->
                <textarea
                  v-show="showHtmlSource"
                  v-model="htmlSource"
                  class="html-source"
                  @input="onHtmlSourceInput"
                  placeholder="HTML 소스를 직접 편집할 수 있습니다"
                ></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">취소</button>
            <button
              class="btn-submit"
              :disabled="!isFormValid || submitting"
              @click="handleSubmit"
            >
              <span v-if="submitting">저장 중...</span>
              <span v-else>{{ isEditing ? '수정하기' : '등록하기' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 참여자 목록 모달 -->
      <div v-if="showParticipantsModal" class="modal-overlay" @click.self="showParticipantsModal = false">
        <div class="modal-container participants-modal">
          <div class="modal-header">
            <h2 class="modal-title">참여자 목록</h2>
            <button class="close-btn" @click="showParticipantsModal = false">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="participants-header">
              <h3 class="event-name">{{ selectedEvent?.title }}</h3>
              <span class="participants-count">총 {{ participants.length }}명</span>
            </div>

            <div v-if="loadingParticipants" class="loading-state small">
              <div class="spinner"></div>
            </div>

            <div v-else-if="participants.length === 0" class="empty-participants">
              <span>아직 참여자가 없습니다</span>
            </div>

            <div v-else class="participants-table-wrapper">
              <table class="participants-table">
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>이름</th>
                    <th>전화번호</th>
                    <th>참여일시</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, idx) in participants" :key="p.id">
                    <td>{{ participants.length - idx }}</td>
                    <td>{{ p.name }}</td>
                    <td>{{ p.phone }}</td>
                    <td>{{ formatDateTime(p.createdAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="export-section">
              <button class="export-btn" @click="exportParticipants">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
                엑셀 다운로드
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 삭제 확인 모달 -->
      <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
        <div class="confirm-modal">
          <div class="confirm-icon">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h3 class="confirm-title">이벤트 삭제</h3>
          <p class="confirm-message">
            "{{ deleteTarget?.title }}"<br/>
            참여자 데이터도 함께 삭제됩니다.<br/>
            정말 삭제하시겠습니까?
          </p>
          <div class="confirm-actions">
            <button class="btn-cancel" @click="showDeleteConfirm = false">취소</button>
            <button class="btn-delete" @click="confirmDelete">삭제</button>
          </div>
        </div>
      </div>

      <!-- 토스트 메시지 -->
      <transition name="toast">
        <div v-if="showToast" class="toast" :class="toastType">
          {{ toastMessage }}
        </div>
      </transition>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useEvent } from '@/composables/common/useEvent'
import { storage } from '@/firebase/firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'EventManageView',
  components: {
    AdminLayout
  },
  setup() {
    const {
      loading,
      getAllEvents,
      createEvent,
      updateEvent,
      toggleEventActive,
      deleteEvent,
      getEventParticipants
    } = useEvent()

    const events = ref([])
    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const submitting = ref(false)

    // WYSIWYG 에디터 관련
    const editorRef = ref(null)
    const showHtmlSource = ref(false)
    const htmlSource = ref('')
    const currentTextColor = ref('#000000')
    const activeFormats = ref({
      bold: false,
      italic: false,
      underline: false
    })

    // 이미지 업로드 관련
    const thumbnailUploading = ref(false)
    const contentImageUploading = ref(false)

    const form = ref({
      title: '',
      content: '',
      startDate: '',
      endDate: '',
      participationType: 'direct',
      externalLink: '',
      thumbnailUrl: ''
    })

    // 참여자 모달
    const showParticipantsModal = ref(false)
    const selectedEvent = ref(null)
    const participants = ref([])
    const loadingParticipants = ref(false)

    // 삭제 확인
    const showDeleteConfirm = ref(false)
    const deleteTarget = ref(null)

    // 토스트
    const showToast = ref(false)
    const toastMessage = ref('')
    const toastType = ref('success')

    // 통계
    const activeCount = computed(() => {
      const today = new Date().toISOString().split('T')[0]
      return events.value.filter(e => {
        if (!e.isActive) return false
        if (!e.endDate) return true
        return e.endDate >= today
      }).length
    })

    const totalParticipants = computed(() =>
      events.value.reduce((sum, e) => sum + (e.participantCount || 0), 0)
    )

    // 폼 유효성
    const isFormValid = computed(() => {
      const base = form.value.title.trim() !== '' &&
        form.value.content.trim() !== '' &&
        form.value.startDate !== ''

      if (form.value.participationType === 'link') {
        return base && form.value.externalLink.trim() !== ''
      }
      return base
    })

    // 이벤트 로드
    const loadEvents = async () => {
      events.value = await getAllEvents()
    }

    // 날짜 포맷
    const formatPeriod = (event) => {
      const start = event.startDate
      const end = event.endDate

      if (!start) return '-'
      if (!end) return `${start} ~`
      return `${start} ~ ${end}`
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 진행 상태 체크
    const isOngoing = (event) => {
      if (!event.isActive) return false
      const today = new Date().toISOString().split('T')[0]
      const started = event.startDate <= today
      const notEnded = !event.endDate || event.endDate >= today
      return started && notEnded
    }

    const isExpired = (event) => {
      if (!event.endDate) return false
      const today = new Date().toISOString().split('T')[0]
      return event.endDate < today
    }

    // 미리보기 콘텐츠
    const getPreviewContent = (content) => {
      if (!content) return ''
      const text = content.replace(/<[^>]*>/g, '')
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }

    // WYSIWYG 에디터 함수들
    const execCommand = (command, value = null) => {
      editorRef.value?.focus()
      document.execCommand(command, false, value)
      updateToolbarState()
      syncEditorContent()
    }

    const isFormatActive = (format) => activeFormats.value[format]

    const updateToolbarState = () => {
      activeFormats.value.bold = document.queryCommandState('bold')
      activeFormats.value.italic = document.queryCommandState('italic')
      activeFormats.value.underline = document.queryCommandState('underline')
    }

    const syncEditorContent = () => {
      if (editorRef.value) {
        form.value.content = editorRef.value.innerHTML
      }
    }

    const onEditorInput = () => syncEditorContent()

    const onKeydown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault()
            execCommand('bold')
            break
          case 'i':
            e.preventDefault()
            execCommand('italic')
            break
          case 'u':
            e.preventDefault()
            execCommand('underline')
            break
        }
      }
    }

    const changeFontSize = (e) => {
      if (e.target.value) {
        execCommand('fontSize', e.target.value)
        e.target.value = ''
      }
    }

    const changeTextColor = () => {
      execCommand('foreColor', currentTextColor.value)
    }

    const insertLink = () => {
      const url = prompt('링크 URL을 입력하세요:', 'https://')
      if (url) {
        const selection = window.getSelection()
        if (selection.toString()) {
          execCommand('createLink', url)
        } else {
          execCommand('insertHTML', `<a href="${url}" target="_blank">링크</a>`)
        }
      }
    }

    const insertHorizontalRule = () => execCommand('insertHorizontalRule')

    const toggleHtmlSource = () => {
      if (showHtmlSource.value) {
        if (editorRef.value) {
          editorRef.value.innerHTML = htmlSource.value
          syncEditorContent()
        }
      } else {
        htmlSource.value = form.value.content
      }
      showHtmlSource.value = !showHtmlSource.value
    }

    const onHtmlSourceInput = () => {
      form.value.content = htmlSource.value
    }

    // 이미지를 webp로 변환 및 압축
    const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            let { width, height } = img

            if (width > maxWidth) {
              height = (height * maxWidth) / width
              width = maxWidth
            }

            canvas.width = width
            canvas.height = height

            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)

            canvas.toBlob(
              (blob) => resolve(blob),
              'image/webp',
              quality
            )
          }
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      })
    }

    // 썸네일 이미지 업로드
    const handleThumbnailUpload = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      if (file.size > 5 * 1024 * 1024) {
        showToastMessage('5MB 이하의 이미지만 업로드 가능합니다.', 'error')
        return
      }

      thumbnailUploading.value = true

      try {
        const compressedBlob = await compressImage(file, 1200, 0.85)
        const fileName = `events/thumbnails/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.webp`
        const fileRef = storageRef(storage, fileName)

        await uploadBytes(fileRef, compressedBlob, { contentType: 'image/webp' })
        const downloadUrl = await getDownloadURL(fileRef)

        form.value.thumbnailUrl = downloadUrl
        showToastMessage('썸네일이 업로드되었습니다.', 'success')
      } catch (err) {
        console.error('썸네일 업로드 실패:', err)
        showToastMessage('이미지 업로드에 실패했습니다.', 'error')
      } finally {
        thumbnailUploading.value = false
        e.target.value = ''
      }
    }

    // 썸네일 제거
    const removeThumbnail = () => {
      form.value.thumbnailUrl = ''
    }

    // 본문 이미지 업로드
    const handleContentImageUpload = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      if (file.size > 5 * 1024 * 1024) {
        showToastMessage('5MB 이하의 이미지만 업로드 가능합니다.', 'error')
        return
      }

      contentImageUploading.value = true

      try {
        const compressedBlob = await compressImage(file, 800, 0.8)
        const fileName = `events/content/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.webp`
        const fileRef = storageRef(storage, fileName)

        await uploadBytes(fileRef, compressedBlob, { contentType: 'image/webp' })
        const downloadUrl = await getDownloadURL(fileRef)

        // 에디터에 이미지 삽입
        editorRef.value?.focus()
        execCommand('insertHTML', `<img src="${downloadUrl}" alt="이미지" style="max-width:100%;border-radius:8px;margin:8px 0;" />`)

        showToastMessage('이미지가 삽입되었습니다.', 'success')
      } catch (err) {
        console.error('이미지 업로드 실패:', err)
        showToastMessage('이미지 업로드에 실패했습니다.', 'error')
      } finally {
        contentImageUploading.value = false
        e.target.value = ''
      }
    }

    const setEditorContent = (content) => {
      nextTick(() => {
        if (editorRef.value) {
          editorRef.value.innerHTML = content || ''
        }
      })
    }

    // 모달 열기 (생성)
    const openCreateModal = () => {
      isEditing.value = false
      editingId.value = null
      showHtmlSource.value = false
      htmlSource.value = ''

      const today = new Date().toISOString().split('T')[0]
      form.value = {
        title: '',
        content: '',
        startDate: today,
        endDate: '',
        participationType: 'direct',
        externalLink: '',
        thumbnailUrl: ''
      }
      showModal.value = true
      setEditorContent('')
    }

    // 모달 열기 (수정)
    const openEditModal = (event) => {
      isEditing.value = true
      editingId.value = event.id
      showHtmlSource.value = false
      htmlSource.value = event.content || ''
      form.value = {
        title: event.title,
        content: event.content,
        startDate: event.startDate,
        endDate: event.endDate || '',
        participationType: event.participationType || 'direct',
        externalLink: event.externalLink || '',
        thumbnailUrl: event.thumbnailUrl || ''
      }
      showModal.value = true
      setEditorContent(event.content)
    }

    // 모달 닫기
    const closeModal = () => {
      showModal.value = false
      showHtmlSource.value = false
    }

    // 제출 처리
    const handleSubmit = async () => {
      if (!isFormValid.value || submitting.value) return

      submitting.value = true

      try {
        if (isEditing.value) {
          await updateEvent(editingId.value, form.value)
          showToastMessage('이벤트가 수정되었습니다.', 'success')
        } else {
          await createEvent(form.value)
          showToastMessage('이벤트가 등록되었습니다.', 'success')
        }

        closeModal()
        await loadEvents()
      } catch (err) {
        showToastMessage('처리 중 오류가 발생했습니다.', 'error')
      } finally {
        submitting.value = false
      }
    }

    // 활성화 토글
    const handleToggleActive = async (event) => {
      try {
        await toggleEventActive(event.id, !event.isActive)
        event.isActive = !event.isActive
        showToastMessage(
          event.isActive ? '이벤트가 활성화되었습니다.' : '이벤트가 비활성화되었습니다.',
          'success'
        )
      } catch (err) {
        showToastMessage('처리 중 오류가 발생했습니다.', 'error')
      }
    }

    // 삭제 요청
    const handleDelete = (event) => {
      deleteTarget.value = event
      showDeleteConfirm.value = true
    }

    // 삭제 확인
    const confirmDelete = async () => {
      if (!deleteTarget.value) return

      try {
        await deleteEvent(deleteTarget.value.id)
        showToastMessage('이벤트가 삭제되었습니다.', 'success')
        showDeleteConfirm.value = false
        deleteTarget.value = null
        await loadEvents()
      } catch (err) {
        showToastMessage('삭제 중 오류가 발생했습니다.', 'error')
      }
    }

    // 참여자 모달 열기
    const openParticipantsModal = async (event) => {
      selectedEvent.value = event
      showParticipantsModal.value = true
      loadingParticipants.value = true

      try {
        participants.value = await getEventParticipants(event.id)
      } catch (err) {
        console.error('참여자 조회 실패:', err)
      } finally {
        loadingParticipants.value = false
      }
    }

    // 엑셀 내보내기
    const exportParticipants = async () => {
      if (participants.value.length === 0) return

      try {
        const xlsx = await import('xlsx')

        const data = participants.value.map((p, idx) => ({
          '번호': participants.value.length - idx,
          '이름': p.name,
          '전화번호': p.phone,
          '참여일시': formatDateTime(p.createdAt)
        }))

        const ws = xlsx.utils.json_to_sheet(data)
        const wb = xlsx.utils.book_new()
        xlsx.utils.book_append_sheet(wb, ws, '참여자 목록')

        const fileName = `${selectedEvent.value.title}_참여자목록_${new Date().toISOString().split('T')[0]}.xlsx`
        xlsx.writeFile(wb, fileName)

        showToastMessage('엑셀 파일이 다운로드되었습니다.', 'success')
      } catch (err) {
        console.error('엑셀 내보내기 실패:', err)
        showToastMessage('엑셀 내보내기에 실패했습니다.', 'error')
      }
    }

    // 토스트 메시지
    const showToastMessage = (message, type = 'success') => {
      toastMessage.value = message
      toastType.value = type
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000)
    }

    onMounted(() => {
      loadEvents()
    })

    return {
      loading,
      events,
      showModal,
      isEditing,
      form,
      submitting,
      isFormValid,
      activeCount,
      totalParticipants,
      showDeleteConfirm,
      deleteTarget,
      showToast,
      toastMessage,
      toastType,
      formatPeriod,
      formatDateTime,
      isOngoing,
      isExpired,
      getPreviewContent,
      openCreateModal,
      openEditModal,
      closeModal,
      handleSubmit,
      handleToggleActive,
      handleDelete,
      confirmDelete,
      // 참여자 모달
      showParticipantsModal,
      selectedEvent,
      participants,
      loadingParticipants,
      openParticipantsModal,
      exportParticipants,
      // WYSIWYG 에디터
      editorRef,
      showHtmlSource,
      htmlSource,
      currentTextColor,
      execCommand,
      isFormatActive,
      updateToolbarState,
      onEditorInput,
      onKeydown,
      changeFontSize,
      changeTextColor,
      insertLink,
      insertHorizontalRule,
      toggleHtmlSource,
      onHtmlSourceInput,
      // 이미지 업로드
      thumbnailUploading,
      handleThumbnailUpload,
      removeThumbnail,
      handleContentImageUpload
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.event-manage {
  @apply p-6 lg:p-8;
}

/* 페이지 헤더 */
.page-header {
  @apply flex items-center justify-between mb-8;
}

.header-info {
  @apply flex items-center gap-3;
}

.header-icon {
  @apply w-12 h-12 bg-rose-600/10 rounded-xl flex items-center justify-center text-rose-600;
}

.page-title {
  @apply text-2xl font-bold text-slate-800;
}

.page-subtitle {
  @apply text-sm text-slate-500;
}

.add-btn {
  @apply flex items-center gap-1.5 px-5 py-2.5 bg-rose-600 text-white text-sm font-semibold rounded-xl hover:bg-rose-700 transition shadow-lg shadow-rose-600/30;
}

/* 통계 */
.stats-section {
  @apply flex gap-4 mb-6;
}

.stat-card {
  @apply flex-1 flex flex-col items-center justify-center py-5 bg-white rounded-xl border border-slate-200 shadow-sm;
}

.stat-value {
  @apply text-3xl font-bold text-slate-800;
}

.stat-label {
  @apply text-sm text-slate-500 mt-1;
}

/* 목록 */
.event-list {
  @apply bg-white rounded-2xl border border-slate-200 shadow-sm p-6;
}

.loading-state,
.empty-state {
  @apply flex flex-col items-center justify-center py-16 text-slate-500;
}

.loading-state.small {
  @apply py-8;
}

.spinner {
  @apply w-8 h-8 border-4 border-slate-200 border-t-rose-600 rounded-full animate-spin mb-4;
}

.empty-icon {
  @apply text-5xl mb-4;
}

.create-first-btn {
  @apply mt-4 px-6 py-3 bg-rose-600 text-white font-semibold rounded-xl hover:bg-rose-700 transition;
}

.event-items {
  @apply space-y-4;
}

.event-item {
  @apply bg-slate-50 rounded-xl p-5 border border-slate-200 transition hover:border-rose-300;
}

.event-item.inactive {
  @apply opacity-60;
}

.event-item.expired {
  @apply bg-slate-100;
}

.event-header {
  @apply flex items-center justify-between mb-3 flex-wrap gap-2;
}

.event-badges {
  @apply flex gap-2 flex-wrap;
}

.badge {
  @apply px-2.5 py-1 text-xs font-semibold rounded-lg;
}

.badge.ongoing {
  @apply bg-green-100 text-green-600;
}

.badge.expired {
  @apply bg-slate-200 text-slate-500;
}

.badge.upcoming {
  @apply bg-amber-100 text-amber-600;
}

.badge.inactive {
  @apply bg-slate-200 text-slate-500;
}

.badge.type {
  @apply bg-blue-100 text-blue-600;
}

.badge.type.link {
  @apply bg-violet-100 text-violet-600;
}

.event-actions {
  @apply flex gap-2 flex-wrap;
}

.action-btn {
  @apply px-3 py-1.5 text-xs font-medium rounded-lg transition;
}

.action-btn.view {
  @apply bg-blue-100 text-blue-600 hover:bg-blue-200;
}

.action-btn.edit {
  @apply bg-slate-200 text-slate-600 hover:bg-slate-300;
}

.action-btn.delete {
  @apply bg-red-100 text-red-600 hover:bg-red-200;
}

.action-btn.activate {
  @apply bg-green-100 text-green-600 hover:bg-green-200;
}

.action-btn.deactivate {
  @apply bg-amber-100 text-amber-600 hover:bg-amber-200;
}

.event-title {
  @apply text-lg font-semibold text-slate-800 mb-2;
}

.event-period {
  @apply flex items-center gap-1.5 text-sm text-slate-500 mb-2;
}

.event-meta {
  @apply flex items-center gap-4 text-sm text-slate-500 mb-3;
}

.meta-item {
  @apply flex items-center gap-1.5;
}

.event-preview {
  @apply text-sm text-slate-600 line-clamp-2;
}

/* 모달 */
.modal-overlay {
  @apply fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4;
}

.modal-container {
  @apply w-full max-w-2xl bg-white rounded-2xl overflow-hidden max-h-[90vh] flex flex-col;
}

.modal-container.participants-modal {
  @apply max-w-3xl;
}

.modal-header {
  @apply flex items-center justify-between px-6 py-4 border-b border-slate-100;
}

.modal-title {
  @apply text-xl font-bold text-slate-800;
}

.close-btn {
  @apply p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition;
}

.modal-body {
  @apply flex-1 overflow-y-auto p-6 space-y-5;
}

.form-group {
  @apply space-y-2;
}

.form-row {
  @apply grid grid-cols-2 gap-4;
}

.form-label {
  @apply block text-sm font-semibold text-slate-700;
}

.required {
  @apply text-red-500;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none transition focus:border-rose-600 focus:ring-2 focus:ring-rose-600/20;
}

.form-hint {
  @apply text-xs text-slate-400 mt-1;
}

/* 썸네일 업로드 */
.thumbnail-upload {
  @apply w-full;
}

.thumbnail-preview {
  @apply relative w-full max-w-xs rounded-xl overflow-hidden border border-slate-200;
}

.thumbnail-preview img {
  @apply w-full h-auto object-cover;
}

.remove-thumbnail {
  @apply absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition;
}

.upload-area {
  @apply flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-rose-400 hover:bg-rose-50/30 transition;
}

.upload-content {
  @apply flex flex-col items-center gap-2 text-slate-500;
}

.upload-hint {
  @apply text-xs text-slate-400;
}

/* 이미지 업로드 버튼 (에디터 툴바) */
.image-upload-btn {
  @apply cursor-pointer;
}

/* 라디오 그룹 */
.radio-group {
  @apply space-y-3;
}

.radio-label {
  @apply flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer transition hover:border-rose-300;
}

.radio-label:has(input:checked) {
  @apply border-rose-600 bg-rose-50;
}

.radio-label input {
  @apply hidden;
}

.radio-mark {
  @apply w-5 h-5 rounded-full border-2 border-slate-300 flex items-center justify-center flex-shrink-0 mt-0.5;
}

.radio-label:has(input:checked) .radio-mark {
  @apply border-rose-600;
}

.radio-label:has(input:checked) .radio-mark::after {
  content: '';
  @apply w-2.5 h-2.5 bg-rose-600 rounded-full;
}

.radio-content {
  @apply flex flex-col;
}

.radio-title {
  @apply font-semibold text-slate-800;
}

.radio-desc {
  @apply text-xs text-slate-500 mt-0.5;
}

/* WYSIWYG 에디터 */
.wysiwyg-editor {
  @apply border border-slate-200 rounded-xl overflow-hidden bg-white;
}

.editor-toolbar {
  @apply flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200;
}

.toolbar-group {
  @apply flex items-center gap-0.5;
}

.toolbar-btn {
  @apply w-8 h-8 flex items-center justify-center text-slate-600 hover:bg-white hover:text-rose-600 rounded-lg transition;
}

.toolbar-btn.active {
  @apply bg-rose-100 text-rose-600;
}

.toolbar-btn.color-btn {
  @apply relative;
}

.color-indicator {
  @apply absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-1 rounded-full;
}

.color-picker-wrapper {
  @apply relative;
}

.color-input {
  @apply absolute inset-0 w-full h-full opacity-0 cursor-pointer;
}

.toolbar-select {
  @apply h-8 px-2 text-xs bg-white border border-slate-200 rounded-lg text-slate-600 outline-none cursor-pointer hover:border-rose-300 focus:border-rose-600;
}

.toolbar-divider {
  @apply w-px h-6 bg-slate-200 mx-1;
}

.editor-content {
  @apply min-h-[250px] max-h-[350px] overflow-y-auto p-4 text-sm text-slate-700 leading-relaxed outline-none;
}

.editor-content:empty::before {
  content: '이벤트 내용을 입력하세요...';
  @apply text-slate-400;
}

.editor-content:focus {
  @apply bg-rose-50/30;
}

.html-source {
  @apply w-full min-h-[250px] max-h-[350px] p-4 text-sm font-mono bg-slate-900 text-green-400 outline-none resize-none;
}

.modal-footer {
  @apply flex gap-3 px-6 py-4 border-t border-slate-100;
}

.btn-cancel {
  @apply flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 transition;
}

.btn-submit {
  @apply flex-1 py-3 bg-rose-600 text-white font-semibold rounded-xl hover:bg-rose-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed;
}

/* 참여자 모달 */
.participants-header {
  @apply flex items-center justify-between mb-4;
}

.event-name {
  @apply text-lg font-semibold text-slate-800;
}

.participants-count {
  @apply text-sm text-slate-500;
}

.empty-participants {
  @apply text-center py-8 text-slate-400;
}

.participants-table-wrapper {
  @apply overflow-x-auto max-h-[400px] overflow-y-auto;
}

.participants-table {
  @apply w-full text-sm;
}

.participants-table th {
  @apply px-4 py-3 bg-slate-100 text-left font-semibold text-slate-600 sticky top-0;
}

.participants-table td {
  @apply px-4 py-3 border-b border-slate-100;
}

.export-section {
  @apply flex justify-end mt-4 pt-4 border-t border-slate-100;
}

.export-btn {
  @apply flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition;
}

/* 삭제 확인 모달 */
.confirm-modal {
  @apply w-full max-w-sm bg-white rounded-2xl p-6 text-center;
}

.confirm-icon {
  @apply flex justify-center mb-4;
}

.confirm-title {
  @apply text-lg font-bold text-slate-800 mb-2;
}

.confirm-message {
  @apply text-sm text-slate-600 mb-6;
}

.confirm-actions {
  @apply flex gap-3;
}

.btn-delete {
  @apply flex-1 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition;
}

/* 토스트 */
.toast {
  @apply fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-sm font-medium shadow-lg z-50;
}

.toast.success {
  @apply bg-green-600 text-white;
}

.toast.error {
  @apply bg-red-600 text-white;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}
</style>
